import { useEffect, useMemo, useState } from 'react';
import { marketingWebAPI } from '../../api/api';
import { Mail, Phone, RefreshCw, Trash2, Download, ExternalLink, Search } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const ESTADOS = [
  { value: 'nuevo', label: 'Nuevo', color: 'bg-blue-100 text-blue-700' },
  { value: 'contactado', label: 'Contactado', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'convertido', label: 'Convertido', color: 'bg-green-100 text-green-700' },
  { value: 'descartado', label: 'Descartado', color: 'bg-gray-200 text-gray-600' },
];

const ORIGENES = [
  { value: 'contacto', label: 'Formulario contacto' },
  { value: 'catalogo', label: 'Descarga catálogo' },
];

function formatDate(iso) {
  if (!iso) return '';
  // El backend serializa datetimes en UTC pero SIN sufijo 'Z' (ISO naive); si lo
  // parseamos tal cual, el navegador lo trata como hora local y se ve un desfase
  // de 2h (Madrid = UTC+2 en verano). Añadimos la 'Z' si no trae zona horaria.
  const tieneZona = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(tieneZona ? iso : iso + 'Z');
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function estadoBadge(estado) {
  const e = ESTADOS.find((x) => x.value === estado) || ESTADOS[0];
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${e.color}`}>
      {e.label}
    </span>
  );
}

function origenLabel(origen) {
  return ORIGENES.find((x) => x.value === origen)?.label || origen;
}

function exportCsv(rows) {
  const headers = [
    'Fecha', 'Origen', 'Nombre', 'Email', 'Teléfono', 'Empresa',
    'Fecha evento', 'Personas', 'Tipo', 'Mensaje', 'Estado', 'Notas', 'URL origen',
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const csv = [
    headers.join(';'),
    ...rows.map((r) => [
      r.created_at, r.origen, r.nombre, r.email, r.telefono, r.empresa,
      r.fecha_evento, r.personas, r.tipo_evento, r.mensaje, r.estado, r.notas, r.source_url,
    ].map(escape).join(';')),
  ].join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leads-web-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function MarketingWebLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ origen: '', estado: '', desde: '', hasta: '', q: '' });
  const [editando, setEditando] = useState(null); // {id, notas, estado}

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.origen) params.origen = filtros.origen;
      if (filtros.estado) params.estado = filtros.estado;
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      const res = await marketingWebAPI.listarLeads(params);
      setLeads(res.data.items || []);
    } catch (e) {
      console.error('Error cargando leads:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // Refresh automático cada 5 min
    const id = setInterval(cargar, 5 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.origen, filtros.estado, filtros.desde, filtros.hasta]);

  const leadsFiltrados = useMemo(() => {
    if (!filtros.q) return leads;
    const q = filtros.q.toLowerCase();
    return leads.filter((l) =>
      (l.nombre || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q) ||
      (l.empresa || '').toLowerCase().includes(q) ||
      (l.telefono || '').toLowerCase().includes(q) ||
      (l.mensaje || '').toLowerCase().includes(q),
    );
  }, [leads, filtros.q]);

  const actualizar = async (id, cambios) => {
    try {
      await marketingWebAPI.actualizarLead(id, cambios);
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...cambios } : l)));
    } catch (e) {
      console.error('Error actualizando lead:', e);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este lead? Esta acción no se puede deshacer.')) return;
    try {
      await marketingWebAPI.eliminarLead(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      console.error('Error eliminando lead:', e);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary">Leads desde la web</h1>
          <p className="text-sm text-gray-500 mt-1">
            Leads capturados desde <span className="font-mono">duettocatering.com</span> · {leadsFiltrados.length} resultados
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={cargar}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2"
            title="Refrescar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </button>
          <button
            onClick={() => exportCsv(leadsFiltrados)}
            className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, email, empresa, teléfono…"
            value={filtros.q}
            onChange={(e) => setFiltros({ ...filtros, q: e.target.value })}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-md"
          />
        </div>
        <select
          value={filtros.origen}
          onChange={(e) => setFiltros({ ...filtros, origen: e.target.value })}
          className="px-3 py-2 text-sm border rounded-md"
        >
          <option value="">Todos los orígenes</option>
          {ORIGENES.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={filtros.estado}
          onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
          className="px-3 py-2 text-sm border rounded-md"
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <input
            type="date"
            value={filtros.desde}
            onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
            className="w-full px-2 py-2 text-xs border rounded-md"
            title="Desde"
          />
          <input
            type="date"
            value={filtros.hasta}
            onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
            className="w-full px-2 py-2 text-xs border rounded-md"
            title="Hasta"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Origen</TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Mensaje</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-400 py-8">Cargando…</TableCell>
              </TableRow>
            )}
            {!loading && leadsFiltrados.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                  No hay leads que coincidan con los filtros.
                </TableCell>
              </TableRow>
            )}
            {leadsFiltrados.map((l) => (
              <TableRow key={l.id} className="hover:bg-gray-50">
                <TableCell className="text-xs whitespace-nowrap">{formatDate(l.created_at)}</TableCell>
                <TableCell className="text-xs">{origenLabel(l.origen)}</TableCell>
                <TableCell>
                  <div className="font-semibold text-sm">{l.nombre}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    <a href={`mailto:${l.email}`} className="hover:underline">{l.email}</a>
                  </div>
                  {l.telefono && (
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <a href={`tel:${l.telefono}`} className="hover:underline">{l.telefono}</a>
                    </div>
                  )}
                  {l.empresa && (
                    <div className="text-xs text-gray-500 mt-0.5">{l.empresa}</div>
                  )}
                  {/* Estado Google Ads (Offline Conversion Import) */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {l.gclid && (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-blue-50 text-blue-700 border border-blue-200" title={`gclid: ${l.gclid}`}>Google Ads</span>
                    )}
                    {l.ads_keyword && (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-700 border border-amber-200" title={l.ads_campaign ? `Campaña: ${l.ads_campaign}` : ''}>
                        kw: {l.ads_keyword}
                      </span>
                    )}
                    {l.presupuesto_id && (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-indigo-50 text-indigo-700 border border-indigo-200" title={`Presupuesto ${l.presupuesto_id}`}>
                        Vinculado{l.valor_evento_eur ? ` · ${Math.round(l.valor_evento_eur)} €` : ''}
                      </span>
                    )}
                    {l.gclid_uploaded_at && (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Subido a Ads</span>
                    )}
                    {l.gclid_upload_error && !l.gclid_uploaded_at && (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-red-50 text-red-700 border border-red-200" title={l.gclid_upload_error}>Error subida</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {l.fecha_evento && <div><strong>Fecha:</strong> {l.fecha_evento}</div>}
                  {l.personas && <div><strong>Pax:</strong> {l.personas}</div>}
                  {l.tipo_evento && <div className="capitalize"><strong>Tipo:</strong> {l.tipo_evento}</div>}
                </TableCell>
                <TableCell className="text-xs max-w-[260px]">
                  <div className="line-clamp-3 text-gray-700">{l.mensaje || '—'}</div>
                  {l.source_url && (
                    <a
                      href={l.source_url.startsWith('http') ? l.source_url : `https://duettocatering.com${l.source_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-gray-400 hover:text-duetto-primary inline-flex items-center gap-1 mt-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {l.source_url}
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <select
                    value={l.estado || 'nuevo'}
                    onChange={(e) => actualizar(l.id, { estado: e.target.value })}
                    className="text-xs border rounded px-2 py-1 mb-1 w-full"
                  >
                    {ESTADOS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <div>{estadoBadge(l.estado)}</div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={() => setEditando(editando?.id === l.id ? null : { id: l.id, notas: l.notas || '' })}
                      className="text-xs text-duetto-primary hover:underline whitespace-nowrap"
                    >
                      {editando?.id === l.id ? 'Cerrar' : 'Notas'}
                    </button>
                    <button
                      onClick={() => eliminar(l.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {editando?.id === l.id && (
                    <div className="mt-2">
                      <textarea
                        value={editando.notas}
                        onChange={(e) => setEditando({ ...editando, notas: e.target.value })}
                        onBlur={() => {
                          actualizar(l.id, { notas: editando.notas });
                          setEditando(null);
                        }}
                        autoFocus
                        rows={3}
                        placeholder="Notas internas sobre este lead…"
                        className="w-56 text-xs border rounded p-2 text-left"
                      />
                    </div>
                  )}
                  {l.notas && editando?.id !== l.id && (
                    <div className="text-[10px] text-gray-500 mt-1 max-w-[180px] line-clamp-2 text-left">
                      {l.notas}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
